import { cn } from '@/lib/utils';
import { EncounterChip } from './EncounterChip';
import type { CalendarEntry } from '../api/calendar-api';

/** Beyond this a cell shows a count instead of more chips. */
const VISIBLE_CHIPS = 3;

/**
 * One day.
 *
 * Cells do not scroll. A scrollable calendar cell hides appointments behind an
 * affordance nobody looks for, which on a clinic schedule is a missed visit — so
 * the overflow is stated as a count instead.
 *
 * `unshown` marks cells past the point where the row cap truncated the query.
 * Those are not empty days, and rendering them as empty would be the one lie a
 * calendar must not tell.
 */
export function CalendarDayCell({
  date,
  entries,
  inMonth,
  isToday,
  unshown,
  selected,
  onSelect,
}: {
  date: string;
  entries: CalendarEntry[];
  inMonth: boolean;
  isToday: boolean;
  unshown: boolean;
  selected: boolean;
  onSelect: (date: string) => void;
}) {
  const overflow = entries.length - VISIBLE_CHIPS;

  return (
    <div
      className={cn(
        'flex min-h-[104px] flex-col gap-1 bg-background p-1.5 md:min-h-[132px] md:p-2',
        !inMonth && 'bg-muted/30',
        unshown && 'bg-warning-surface/40',
        selected && 'ring-2 ring-inset ring-primary',
      )}
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-pressed={selected}
          aria-label={`View schedule for ${date}`}
          onClick={() => onSelect(date)}
          className={cn(
            'grid size-6 place-items-center rounded-full text-[12px] tabular-nums hover:bg-accent focus-visible:outline-2 focus-visible:outline-primary',
            isToday && 'bg-primary font-semibold text-primary-foreground',
            !inMonth && !isToday && 'text-muted-foreground/60',
          )}
        >
          {Number(date.slice(8, 10))}
        </button>
      </div>

      {unshown ? (
        <span className="text-[10px] leading-tight text-muted-foreground">
          Too many to show — filter by clinician
        </span>
      ) : (
        <>
          {entries.slice(0, VISIBLE_CHIPS).map((entry) => (
            <EncounterChip key={entry.id} entry={entry} />
          ))}
          {overflow > 0 && (
            <button type="button" className="px-1.5 text-left text-[10px] text-primary hover:underline" onClick={() => onSelect(date)}>
              +{overflow} more
            </button>
          )}
        </>
      )}
    </div>
  );
}
